import { useRef, useState } from "react";
import { Microphone, Stop, Spinner } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Records audio via MediaRecorder, sends to /api/ai/transcribe,
 * calls onTranscript(text) with the returned transcription.
 */
export default function VoiceRecorder({ onTranscript, disabled }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const start = async () => {
    if (recording || busy || disabled) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported on this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        clearInterval(timerRef.current);
        setSeconds(0);
        if (blob.size < 500) return; // ignore very short taps
        setBusy(true);
        try {
          const fd = new FormData();
          const ext = (rec.mimeType || "audio/webm").includes("mp4") ? "mp4" : "webm";
          fd.append("file", blob, `voice.${ext}`);
          const { data } = await api.post("/ai/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (data.text) {
            onTranscript(data.text);
            toast.success("Transcribed");
          } else {
            toast.error("Nothing detected");
          }
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transcription failed");
        } finally {
          setBusy(false);
        }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") {
      try { recRef.current.stop(); } catch (err) { console.warn("MediaRecorder stop failed (likely already stopped)", err); }
    }
    setRecording(false);
  };

  const label = busy ? "Transcribing..." : recording ? `Recording ${seconds}s — tap to stop` : "Voice";

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={disabled || busy}
      data-testid="voice-recorder-btn"
      className={`h-12 px-4 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-40 ${
        recording ? "bg-red-500 text-white animate-pulse" : busy ? "bg-slate-200" : "bg-white"
      }`}
    >
      {busy ? <Spinner weight="bold" size={16} className="animate-spin"/> : recording ? <Stop weight="fill" size={16}/> : <Microphone weight="bold" size={16}/>}
      <span>{label}</span>
    </button>
  );
}
